import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import WelcomeModal from './WelcomeModal';
import './AuthModal.css';
import './ClaimAccount.css';

export default function AuthModal({ onClose, defaultTab = 'signin', onSuccess, heading, signupRole }) {
  const { signInWithGoogle, signInWithEmail, signUpWithEmail, resetPassword, isGuest, guestProfile } = useAuth();
  // A guest with posts keeps them on either tab: signing up links onto the
  // guest account, signing in merges it (see AuthContext). Worth saying, since
  // "Sign In" otherwise reads like it would leave them behind.
  const guestHandle = isGuest ? guestProfile?.handle : null;
  const [tab, setTab] = useState(defaultTab);
  const [view, setView] = useState('auth'); // 'auth' | 'forgot'
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false); // shown once after a fresh signup
  const [welcomeName, setWelcomeName] = useState('');
  const overlayRef = useRef(null);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleGoogle() {
    setError('');
    setLoading(true);
    try {
      const { user, isNewUser } = await signInWithGoogle(signupRole);
      if (isNewUser) {
        setWelcomeName((user?.displayName || '').trim().split(' ')[0] || '');
        setShowWelcome(true);
      } else {
        onClose();
        onSuccess?.();
      }
    } catch (e) {
      setError(friendlyError(e.code, isGuest));
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (tab === 'signin') {
        await signInWithEmail(email, password);
        onClose();
        onSuccess?.();
      } else {
        if (!displayName.trim()) { setError('Name is required.'); setLoading(false); return; }
        await signUpWithEmail(email, password, displayName.trim(), signupRole);
        setWelcomeName(displayName.trim().split(' ')[0] || '');
        setShowWelcome(true);
      }
    } catch (e) {
      setError(friendlyError(e.code, isGuest));
    } finally {
      setLoading(false);
    }
  }

  function handleWelcomeClose() {
    setShowWelcome(false);
    onClose();
    onSuccess?.();
  }

  async function handleForgot(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      await resetPassword(email);
      setSuccess('Check your email for a password reset link.');
    } catch (e) {
      setError(friendlyError(e.code));
    } finally {
      setLoading(false);
    }
  }

  function handleOverlayClick(e) {
    if (e.target === overlayRef.current) onClose();
  }

  function goToForgot() {
    setView('forgot');
    setError('');
    setSuccess('');
  }

  function goToAuth() {
    setView('auth');
    setError('');
    setSuccess('');
  }

  if (showWelcome) {
    return <WelcomeModal onClose={handleWelcomeClose} firstName={welcomeName} />;
  }

  return (
    <div className="auth-overlay" ref={overlayRef} onClick={handleOverlayClick} role="dialog" aria-modal="true">
      <div className="auth-modal">
        <button className="auth-modal__close" onClick={onClose} aria-label="Close">×</button>
        <img src="/assets/logo.png" alt="Volcyy" className="auth-modal__logo" />

        {heading && <h2 className="auth-modal__heading">{heading}</h2>}

        {guestHandle && view === 'auth' && (
          <p className="auth-modal__guest-note">
            Everything you posted as <strong>{guestHandle}</strong> moves to this account.
          </p>
        )}

        {view === 'forgot' ? (
          <>
            <h2 className="auth-modal__forgot-title">Reset Password</h2>
            <p className="auth-modal__forgot-desc">Enter your email and we'll send you a reset link.</p>

            <form onSubmit={handleForgot} autoComplete="on">
              <div className="auth-modal__field">
                <label htmlFor="auth-reset-email">Email *</label>
                <input
                  id="auth-reset-email"
                  type="email"
                  autoComplete="email"
                  placeholder="John@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </div>

              {error && <p className="auth-modal__error" role="alert">{error}</p>}
              {success && <p className="auth-modal__success" role="status">{success}</p>}

              <button className="auth-modal__submit" type="submit" disabled={loading}>
                {loading ? 'Sending…' : 'Send Reset Link'}
              </button>
            </form>

            <button className="auth-modal__back-link" onClick={goToAuth}>
              ← Back to Sign In
            </button>
          </>
        ) : (
          <>
            <div className="auth-modal__tabs" role="tablist">
              <button
                role="tab"
                aria-selected={tab === 'signin'}
                className={tab === 'signin' ? 'active' : ''}
                onClick={() => { setTab('signin'); setError(''); }}
              >Sign In</button>
              <button
                role="tab"
                aria-selected={tab === 'signup'}
                className={tab === 'signup' ? 'active' : ''}
                onClick={() => { setTab('signup'); setError(''); }}
              >Sign Up</button>
            </div>

            <button className="auth-modal__google" onClick={handleGoogle} disabled={loading}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                <path d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 6.294C4.672 4.167 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>

            <div className="auth-modal__divider"><span>or</span></div>

            <form onSubmit={handleSubmit} autoComplete="on">
              {tab === 'signup' && (
                <div className="auth-modal__field">
                  <label htmlFor="auth-name">Full Name *</label>
                  <input
                    id="auth-name"
                    type="text"
                    autoComplete="name"
                    placeholder="John Smith"
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    required
                  />
                </div>
              )}
              <div className="auth-modal__field">
                <label htmlFor="auth-email">Email *</label>
                <input
                  id="auth-email"
                  type="email"
                  autoComplete="email"
                  placeholder="John@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="auth-modal__field">
                <label htmlFor="auth-password">Password *</label>
                <div className="auth-modal__password-wrap">
                  <input
                    id="auth-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete={tab === 'signin' ? 'current-password' : 'new-password'}
                    placeholder={tab === 'signup' ? 'At least 6 characters' : ''}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    className="auth-modal__pw-toggle"
                    onClick={() => setShowPassword(s => !s)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              {tab === 'signin' && (
                <div className="auth-modal__forgot-row">
                  <button type="button" className="auth-modal__forgot-btn" onClick={goToForgot}>
                    Forgot password?
                  </button>
                </div>
              )}

              {error && <p className="auth-modal__error" role="alert">{error}</p>}

              <button className="auth-modal__submit" type="submit" disabled={loading}>
                {loading ? 'Please wait…' : tab === 'signin' ? 'Sign In' : 'Create Account'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M1 9s3-5.5 8-5.5S17 9 17 9s-3 5.5-8 5.5S1 9 1 9z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="9" cy="9" r="2.2" stroke="currentColor" strokeWidth="1.4"/>
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M7.2 4.1A6.9 6.9 0 019 3.5C14 3.5 17 9 17 9a13 13 0 01-2.2 2.85M4.5 5.2A13 13 0 001 9s3 5.5 8 5.5a6.9 6.9 0 003.3-.85" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7.4 7.4a2.2 2.2 0 003.1 3.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 2l14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}

function friendlyError(code, isGuest) {
  switch (code) {
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Incorrect email or password.';
    case 'auth/email-already-in-use':
    case 'auth/credential-already-in-use':
      return isGuest
        ? 'That email already has an account. Sign in instead — your posts will move over.'
        : 'An account with this email already exists.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/popup-closed-by-user':
      return 'Sign-in was cancelled.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    default:
      return 'Something went wrong. Please try again.';
  }
}
